import { Checkbox, type CheckboxChangeEventType } from '@myosh/odin-components';
import { useReactFlow } from '@xyflow/react';
import { forwardRef, type Ref, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  useUpdateGeneratedDiagramFiltersMutation,
  useUpdateStoredDiagramFiltersMutation,
} from '../../../api/enhanced/enhanced-bowtie-api';
import { useDiagramContext } from '../../../context/diagram.context';
import useDiagramFilterData from '../../../hooks/use-diagram-filter-data';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectShowControlEffectiveness,
  selectShowControlsInSingleRow,
  selectShowControlTypes,
  selectShowOnlyCriticalControls,
  setFilter,
} from '../../../redux/slices/diagram';

interface DisplayFilterTabProps {}

export interface DisplayFilterTabRef {
  save: () => void;
}

function DisplayFilterTab(_props: DisplayFilterTabProps, ref: Ref<DisplayFilterTabRef>) {
  const showOnlyCriticalControlsState = useAppSelector(selectShowOnlyCriticalControls);
  const showControlTypesState = useAppSelector(selectShowControlTypes);
  const showControlEffectivenessState = useAppSelector(selectShowControlEffectiveness);
  const showControlsInSingleRowState = useAppSelector(selectShowControlsInSingleRow);

  const showOnlyCriticalControlsRef = useRef<boolean>(showOnlyCriticalControlsState);
  const showControlTypesRef = useRef<boolean>(showControlTypesState);
  const showControlEffectivenessRef = useRef<boolean>(showControlEffectivenessState);
  const showControlsInSingleRowRef = useRef<boolean>(showControlsInSingleRowState);

  useImperativeHandle(ref, () => ({
    save: applyClickHandler,
  }));

  const dispatch = useAppDispatch();
  const { fitView } = useReactFlow();

  const { diagramId, recordId } = useDiagramContext();
  const { diagramFilterData, loading } = useDiagramFilterData();

  const [updateStoredDiagramFilters, { isLoading: isUpdatingStoredDiagramFilters }] =
    useUpdateStoredDiagramFiltersMutation();

  const [updateGeneratedDiagramFilters, { isLoading: isUpdatingGeneratedDiagramFilters }] =
    useUpdateGeneratedDiagramFiltersMutation();

  useEffect(() => {
    if (!loading && !isUpdatingGeneratedDiagramFilters && !isUpdatingStoredDiagramFilters && diagramFilterData) {
      showOnlyCriticalControlsRef.current = diagramFilterData.showOnlyCriticalControls;
      showControlTypesRef.current = diagramFilterData.showControlTypes;
      showControlEffectivenessRef.current = diagramFilterData.showControlEffectiveness;
      showControlsInSingleRowRef.current = diagramFilterData.showControlsInSingleRow;

      dispatchSetFilter();
    }
  }, [diagramFilterData, loading, isUpdatingGeneratedDiagramFilters, isUpdatingStoredDiagramFilters]);

  const dispatchSetFilter = useCallback(() => {
    dispatch(
      setFilter({
        showOnlyCriticalControls: showOnlyCriticalControlsRef.current,
        showControlTypes: showControlTypesRef.current,
        showControlEffectiveness: showControlEffectivenessRef.current,
        showControlsInSingleRow: showControlsInSingleRowRef.current,
      })
    );
  }, []);

  const handleShowOnlyCriticalControlsChange = useCallback((value: CheckboxChangeEventType | undefined) => {
    showOnlyCriticalControlsRef.current = value?.checked as boolean;
  }, []);

  const handleShowControlTypesChange = useCallback((value: CheckboxChangeEventType | undefined) => {
    showControlTypesRef.current = value?.checked as boolean;
  }, []);

  const handleShowControlEffectivenessChange = useCallback((value: CheckboxChangeEventType | undefined) => {
    showControlEffectivenessRef.current = value?.checked as boolean;
  }, []);

  const handleShowControlsInSingleRowChange = useCallback((value: CheckboxChangeEventType | undefined) => {
    showControlsInSingleRowRef.current = value?.checked as boolean;
  }, []);

  const applyClickHandler = useCallback(() => {
    // optimistically update the filters in the store
    dispatchSetFilter();
    setTimeout(fitView, 0);

    let updateFilters = undefined;
    let id = undefined;

    if (recordId) {
      id = recordId;
      updateFilters = updateStoredDiagramFilters;
    } else if (diagramId) {
      id = diagramId;
      updateFilters = updateGeneratedDiagramFilters;
    }

    // update the filters in the backend
    if (updateFilters && id) {
      toast.promise(
        updateFilters({
          id,
          diagramFilterDto: {
            displayFilter: {
              showOnlyCriticalControls: showOnlyCriticalControlsRef.current,
              showControlTypes: showControlTypesRef.current,
              showControlEffectiveness: showControlEffectivenessRef.current,
              showControlsInSingleRow: showControlsInSingleRowRef.current,
            },
          },
        })
          .unwrap()
          .finally(() => setTimeout(fitView, 0)),
        {
          pending: 'Saving filters...',
          success: { render: 'Filters saved', autoClose: 1200, type: 'info' },
          error: 'Unable to save filters',
        }
      );
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      <Checkbox
        data-cy="checkbox-show-only-critical-controls"
        initialChecked={showOnlyCriticalControlsState}
        name="showOnlyCriticalControls"
        label="Show only Critical Controls"
        onChange={handleShowOnlyCriticalControlsChange}
      />
      <Checkbox
        data-cy="checkbox-show-control-types"
        initialChecked={showControlTypesState}
        name="showControlTypes"
        label="Show Control Types"
        onChange={handleShowControlTypesChange}
      />
      <Checkbox
        data-cy="checkbox-show-control-effectiveness"
        initialChecked={showControlEffectivenessState}
        name="showControlEffectiveness"
        label="Show Control Effectiveness"
        onChange={handleShowControlEffectivenessChange}
      />
      <Checkbox
        data-cy="checkbox-show-controls-in-single-row"
        initialChecked={showControlsInSingleRowState}
        name="showControlsInSingleRow"
        label="Show Controls in a Single Row"
        onChange={handleShowControlsInSingleRowChange}
      />
    </div>
  );
}

export default forwardRef(DisplayFilterTab);
